import { useState } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
} from '@tanstack/react-table'
import { ExternalLink } from 'lucide-react'

interface Engager {
  url: string
  name: string
  title?: string
  company?: string
  engagement_count: number
  engagement_types?: string[]
  recent_post_ids?: string[]
  provider_id?: string
}

const columnHelper = createColumnHelper<Engager>()

const columns = [
  columnHelper.accessor('name', {
    header: 'Name',
    cell: (info) => <span className="font-medium">{info.getValue()}</span>,
  }),
  columnHelper.accessor('title', {
    header: 'Title',
    cell: (info) => (
      <span className="text-sm text-slate-400 max-w-xs truncate block">
        {info.getValue() || '-'}
      </span>
    ),
  }),
  columnHelper.accessor('company', {
    header: 'Company',
    cell: (info) => <span className="text-sm">{info.getValue() || '-'}</span>,
  }),
  columnHelper.accessor('engagement_types', {
    header: 'Types',
    cell: (info) => (
      <span className="text-xs text-slate-300">
        {(info.getValue() || []).join(', ') || '-'}
      </span>
    ),
  }),
  columnHelper.accessor('engagement_count', {
    header: 'Engagements',
    cell: (info) => (
      <span className="text-xs font-mono text-orange-400 font-medium">
        {info.getValue()}
      </span>
    ),
  }),
  columnHelper.accessor('recent_post_ids', {
    header: 'Recent Posts',
    cell: (info) => (
      <span className="font-mono text-xs text-slate-300">
        {(info.getValue() || []).length}
      </span>
    ),
  }),
  columnHelper.accessor('url', {
    header: 'Profile',
    cell: (info) => (
      <a
        href={info.getValue()}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-400 hover:text-blue-300 inline-flex items-center gap-1"
      >
        <ExternalLink className="w-3 h-3" />
        View
      </a>
    ),
  }),
]

interface EngagersTableProps {
  engagers: Engager[]
}

export function EngagersTable({ engagers }: EngagersTableProps) {
  const [sorting, setSorting] = useState<SortingState>([{ id: 'engagement_count', desc: true }])
  const [globalFilter, setGlobalFilter] = useState('')

  const table = useReactTable({
    data: engagers,
    columns,
    state: {
      sorting,
      globalFilter,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <input
          value={globalFilter ?? ''}
          onChange={(e) => setGlobalFilter(e.target.value)}
          className="px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-md text-sm"
          placeholder="Search engagers..."
        />
        <span className="text-xs text-slate-500">{engagers.length} engagers</span>
      </div>

      <div className="border border-slate-800 rounded-md overflow-hidden">
        <div className="max-w-full overflow-auto max-h-96">
          <table className="min-w-[820px] w-full text-sm">
            <thead className="bg-slate-900 sticky top-0">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-3 py-2 text-left text-xs font-medium text-slate-400 uppercase tracking-normal cursor-pointer hover:bg-slate-800"
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-slate-800">
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="hover:bg-slate-900">
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-3 py-2">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
